/**
 * Extracts a LegendEntry from fill, line, and circle layers by reading their
 * primary color paint property (fill-color, line-color, circle-color).
 *
 * Supported expression forms:
 *   - static string         → StaticLegendEntry
 *   - ["match", ...]        → MatchLegendEntry
 *   - ["step", ...]         → StepLegendEntry
 *   - ["interpolate", ...]  → InterpolateLegendEntry
 *   - ["case", ...]         → MatchLegendEntry (treated as categorical fallback list)
 *
 * Returns null for unsupported layer types, missing paint, or unrecognised expressions.
 */

import type { ExpressionSpecification, LayerSpecification } from "maplibre-gl";

import type {
  InterpolateLegendEntry,
  LegendEntry,
  MatchLegendEntry,
  StaticLegendEntry,
  StepLegendEntry,
} from "../types/legend-types";

import { evalColor, evalNumber } from "./evaluate-expression";
import {
  applyOpacityToColor,
  getColorPaint,
  getLayerLabel,
  getSourceLayer,
  getSwatchType,
} from "./layer-utils";

export function extractLegendEntry(layer: LayerSpecification): LegendEntry | null {
  const swatchType = getSwatchType(layer);
  if (!swatchType) return null;

  const colorPaint = getColorPaint(layer);
  if (!colorPaint) return null;

  const layerId = layer.id;
  const layerLabel = getLayerLabel(layer);
  const sourceLayer = getSourceLayer(layer);

  // ---- Static value -------------------------------------------------------
  if (typeof colorPaint === "string") {
    let opacity = 1;
    if (layer.type === "fill") {
      opacity = evalNumber(layer.paint?.["fill-opacity"]) ?? 1;
    } else if (layer.type === "circle") {
      opacity = evalNumber(layer.paint?.["circle-opacity"]) ?? 1;
    }
    const color = applyOpacityToColor(colorPaint, opacity);

    let rawBorderColor: string | undefined;
    if (layer.type === "fill") {
      rawBorderColor = evalColor(layer.paint?.["fill-outline-color"]);
    } else if (layer.type === "circle") {
      rawBorderColor = evalColor(layer.paint?.["circle-stroke-color"]);
    }
    const borderOpacity =
      layer.type === "circle" ? evalNumber(layer.paint?.["circle-stroke-opacity"]) ?? 1 : 1;
    const borderColor = rawBorderColor
      ? applyOpacityToColor(rawBorderColor, borderOpacity)
      : undefined;
    const borderWidth =
      layer.type === "circle" ? evalNumber(layer.paint?.["circle-stroke-width"]) : undefined;

    const entry: StaticLegendEntry = {
      kind: "static",
      layerId,
      layerLabel,
      sourceLayer,
      color,
      swatchType,
      borderColor,
      borderWidth,
    };
    return entry;
  }

  if (!Array.isArray(colorPaint)) return null;

  const expr = colorPaint as ExpressionSpecification;

  // ---- match: ["match", input, val1, color1, val2, color2, ..., fallback] --
  if (expr[0] === "match") {
    const values: MatchLegendEntry["values"] = [];
    for (let i = 2; i < expr.length - 1; i += 2) {
      const rawLabel = expr[i];
      const label = Array.isArray(rawLabel)
        ? (rawLabel as (string | number | boolean)[]).map(String).join(", ")
        : String(rawLabel);
      values.push({ label, color: String(expr[i + 1]) });
    }
    values.push({ label: "other", color: String(expr[expr.length - 1]) });
    const entry: MatchLegendEntry = {
      kind: "match",
      layerId,
      layerLabel,
      sourceLayer,
      swatchType,
      values,
    };
    return entry;
  }

  // ---- step: ["step", input, default, stop1, color1, stop2, color2, ...] ---
  if (expr[0] === "step") {
    const steps: StepLegendEntry["steps"] = [];
    const firstStop = expr[3];
    steps.push({ label: `< ${firstStop}`, color: String(expr[2]) });
    for (let i = 3; i < expr.length - 1; i += 2) {
      const thisStop = expr[i];
      const nextStop = expr[i + 2];
      const label = nextStop !== undefined ? `${thisStop} – ${nextStop}` : `≥ ${thisStop}`;
      steps.push({ label, color: String(expr[i + 1]) });
    }
    const entry: StepLegendEntry = {
      kind: "step",
      layerId,
      layerLabel,
      sourceLayer,
      swatchType,
      steps,
    };
    return entry;
  }

  // ---- interpolate: ["interpolate", interp, input, stop1, color1, ...] -----
  if (expr[0] === "interpolate") {
    const stops: InterpolateLegendEntry["stops"] = [];
    for (let i = 3; i < expr.length - 1; i += 2) {
      stops.push({ stop: Number(expr[i]), color: String(expr[i + 1]) });
    }
    const entry: InterpolateLegendEntry = {
      kind: "interpolate",
      layerId,
      layerLabel,
      sourceLayer,
      swatchType,
      stops,
    };
    return entry;
  }

  // ---- case: ["case", cond1, out1, cond2, out2, ..., fallback] -------------
  // Treated as a categorical list since conditions are not evaluated.
  if (expr[0] === "case") {
    const values: MatchLegendEntry["values"] = [];
    for (let i = 1; i < expr.length - 1; i += 2) {
      values.push({ label: `case ${Math.floor(i / 2) + 1}`, color: String(expr[i + 1]) });
    }
    values.push({ label: "other", color: String(expr[expr.length - 1]) });
    const entry: MatchLegendEntry = {
      kind: "match",
      layerId,
      layerLabel,
      sourceLayer,
      swatchType,
      values,
    };
    return entry;
  }

  console.warn(
    `Unsupported paint legend expression for color paint in layer ${layer.id}:`,
    colorPaint
  );

  return null;
}
